import { Component, signal, ChangeDetectionStrategy, input, output, inject, OnInit, DestroyRef, viewChild, ElementRef } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { L10N_LOCALE, L10nTranslationModule } from 'angular-l10n';
import { debounceTime, switchMap, catchError, of, Observable } from 'rxjs';
import { GroupTreeNode, GroupsApi, PagedGroups } from 'src/app/hickory-api';
import { GroupsSelectDialogComponent } from '../groups-select-dialog/groups-select-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-groups-quick-select',
  templateUrl: './groups-quick-select.component.html',
  styleUrls: ['./groups-quick-select.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    L10nTranslationModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupsQuickSelectComponent implements OnInit {
  readonly tenantId = input<number>();
  readonly classification = input<('Public' | 'Internal') | undefined>();
  readonly selectionChange = output<GroupTreeNode[]>();

  readonly groupInput = viewChild.required<ElementRef<HTMLInputElement>>('groupInput');

  readonly selectedGroups = signal<GroupTreeNode[]>([]);
  readonly searchControl = new FormControl('');
  readonly filteredGroups = signal<GroupTreeNode[]>([]);

  private readonly destroyRef = inject(DestroyRef);
  private readonly groupsApi = inject(GroupsApi);
  private readonly dialog = inject(MatDialog);
  public readonly locale = inject(L10N_LOCALE);

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        switchMap((term) => this.searchGroups(term)),
        catchError(() => of([])),
      )
      .subscribe((groups) => this.filteredGroups.set(groups));
  }

  private searchGroups(term: string | null): Observable<GroupTreeNode[]> {
    if (!term || typeof term !== 'string' || term.length < 2) {
      return of([]);
    }

    return this.groupsApi
      .getGroupsV4(this.tenantId() ?? 0, undefined, undefined, 1, 20, term, this.classification())
      .pipe(
        switchMap((response: PagedGroups) => of(response.items ?? [])),
        catchError(() => of([])),
      );
  }

  onGroupSelected(event: MatAutocompleteSelectedEvent): void {
    const group: GroupTreeNode = event.option.value;

    this.selectedGroups.update((groups) => {
      if (!groups.some((g) => g.id === group.id)) {
        return [...groups, group];
      }
      return groups;
    });

    this.searchControl.setValue('', { emitEvent: false });
    this.groupInput().nativeElement.value = '';
    this.selectionChange.emit(this.selectedGroups());
  }

  removeGroup(group: GroupTreeNode): void {
    this.selectedGroups.update((groups) => groups.filter((g) => g.id !== group.id));
    this.selectionChange.emit(this.selectedGroups());
  }

  clear(): void {
    this.selectedGroups.set([]);
    this.searchControl.setValue('', { emitEvent: false });
    this.groupInput().nativeElement.value = '';
    this.filteredGroups.set([]);
  }

  openAdvancedDialog(): void {
    const dialogRef = this.dialog.open(GroupsSelectDialogComponent, {
      data: {
        existingGroups: this.selectedGroups(),
        classification: this.classification(),
        tenantId: this.tenantId(),
      },
      maxWidth: '1000px',
      minWidth: '600px',
      width: '75%',
      height: '80%',
    });

    dialogRef.afterClosed().subscribe((result: GroupTreeNode[]) => {
      if (result) {
        this.selectedGroups.set(result);
        this.selectionChange.emit(result);
      }
    });
  }

  displayGroupFn(group: GroupTreeNode): string {
    return group?.name || '';
  }
}
